// Step 5d Phase 2 — client offline op queue (v2 §3.1).
// Step 5d Phase 4 — encrypted at rest. The queue KEY stays plaintext (`${user_id}:${seq6}`
// — routing/ordering metadata, needed for per-user FIFO range scans); the op CONTENT
// (client_op_id, device_id, op_type, payload, read_stamp, client_created_at) is AES-GCM
// encrypted under the device DEK. Stored row: { key, user_id, seq, env }.
//
// MIGRATION-SCOPED DUAL SHAPE: op_queue is preserved across the v3 upgrade (ruling #4 —
// non-re-fetchable un-synced work), so legacy PLAINTEXT v2 rows can exist transiently.
// The reader tolerates both; `reencryptLegacyOps()` runs once post-enrolment to convert
// them, after which zero plaintext rows remain (verification post-condition). This is NOT
// a permanent accept-plaintext path.

import { getOfflineDb, type QueuedOp } from './db';
import { getDek } from './dekSession';
import { encryptJson, decryptJson, isEnvelope, type Envelope } from './crypto';
import { apiFetch } from '../api';

const SYNC_BATCH_MAX = 25;
const DEVICE_ID_KEY = 'avsuite-device-id';

function warn(op: string, e: unknown): void {
  console.warn(`[offline] ${op} failed — continuing without queue`, e);
}

// Stable per-browser device id. Phase 4 enrolment registers THIS id with the server's
// device registry; revocation + purge key on it.
export function getDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) { id = crypto.randomUUID(); localStorage.setItem(DEVICE_ID_KEY, id); }
    return id;
  } catch {
    return 'unknown-device';
  }
}
export function resetDeviceId(): void {
  try { localStorage.removeItem(DEVICE_ID_KEY); } catch { /* ignore */ }
}

const keyFor = (userId: string, seq: number): string => `${userId}:${String(seq).padStart(6, '0')}`;
const userRange = (userId: string): IDBKeyRange => IDBKeyRange.bound(`${userId}:`, `${userId}:￿`);

// Stored row shapes: encrypted (Phase 4) vs legacy plaintext (pre-v3, transitional).
interface EncRow { key: string; user_id: string; seq: number; env: Envelope }
type StoredRow = EncRow | QueuedOp;
const encPayload = (op: QueuedOp) => ({ client_op_id: op.client_op_id, device_id: op.device_id, op_type: op.op_type, payload: op.payload, read_stamp: op.read_stamp, client_created_at: op.client_created_at, staleness_advised: op.staleness_advised });

async function decodeRow(dek: Uint8Array, row: StoredRow): Promise<QueuedOp | null> {
  if (isEnvelope((row as EncRow).env)) {
    const r = row as EncRow;
    try {
      const body = await decryptJson<Omit<QueuedOp, 'key' | 'user_id' | 'seq'>>(dek, r.env);
      return { key: r.key, user_id: r.user_id, seq: r.seq, ...body };
    } catch { return null; } // unreadable (wrong DEK / tamper) — skip
  }
  return row as QueuedOp; // legacy plaintext (transitional) — returned as-is
}

async function encodeRow(dek: Uint8Array, op: QueuedOp): Promise<EncRow> {
  return { key: op.key, user_id: op.user_id, seq: op.seq, env: await encryptJson(dek, encPayload(op)) };
}

// ── Change notification (drives the board's provisional overlay) ──────────────
type QueueListener = () => void;
const listeners = new Set<QueueListener>();
export function subscribeOpQueue(l: QueueListener): () => void { listeners.add(l); return () => { listeners.delete(l); }; }
function notify(): void { for (const l of listeners) l(); }

// ── Queue operations ──────────────────────────────────────────────────────────

export async function listQueuedOps(userId: string): Promise<QueuedOp[]> {
  const dek = getDek();
  if (!dek) return []; // locked — queue is opaque
  try {
    const db = await getOfflineDb();
    const rows = (await db.getAll('op_queue', userRange(userId))) as StoredRow[]; // key order = FIFO
    const out: QueuedOp[] = [];
    for (const row of rows) { const op = await decodeRow(dek, row); if (op) out.push(op); }
    return out;
  } catch (e) {
    warn('listQueuedOps', e);
    return [];
  }
}

export async function enqueueOp(
  userId: string, opType: QueuedOp['op_type'], payload: Record<string, unknown>, readStamp: string | null,
  // 5d Phase 5 — staleness lens (client-only; never forwarded to the server). Set by the
  // four call sites when a per-action advisory fired at queue time.
  stalenessAdvised?: QueuedOp['staleness_advised'],
): Promise<QueuedOp | null> {
  const dek = getDek();
  if (!dek) { warn('enqueueOp', 'no DEK (locked) — cannot queue'); return null; }
  try {
    const db = await getOfflineDb();
    const existing = await db.getAllKeys('op_queue', userRange(userId));
    const lastSeq = existing.length > 0 ? Number((existing[existing.length - 1] as string).split(':').pop()) : 0;
    const op: QueuedOp = {
      key: keyFor(userId, lastSeq + 1), user_id: userId, seq: lastSeq + 1,
      client_op_id: crypto.randomUUID(), device_id: getDeviceId(), op_type: opType,
      payload, read_stamp: readStamp, client_created_at: new Date().toISOString(),
      ...(stalenessAdvised ? { staleness_advised: stalenessAdvised } : {}),
    };
    await db.put('op_queue', await encodeRow(dek, op));
    notify();
    return op;
  } catch (e) {
    warn('enqueueOp', e);
    return null;
  }
}

export async function amendQueuedOp(queueKey: string, payload: Record<string, unknown>, readStamp: string | null): Promise<boolean> {
  const dek = getDek();
  if (!dek) return false;
  try {
    const db = await getOfflineDb();
    const row = (await db.get('op_queue', queueKey)) as StoredRow | undefined;
    if (!row) return false;
    const op = await decodeRow(dek, row);
    if (!op) return false;
    await db.put('op_queue', await encodeRow(dek, { ...op, payload, read_stamp: readStamp }));
    notify();
    return true;
  } catch (e) {
    warn('amendQueuedOp', e);
    return false;
  }
}

export async function removeQueuedOp(queueKey: string): Promise<void> {
  try { const db = await getOfflineDb(); await db.delete('op_queue', queueKey); notify(); }
  catch (e) { warn('removeQueuedOp', e); }
}

// One-time post-enrolment pass: re-encrypt any legacy PLAINTEXT op_queue rows carried
// across the v3 upgrade. After this, zero plaintext rows remain (verification post-cond).
// Strictly migration cleanup — not a steady-state path.
export async function reencryptLegacyOps(): Promise<number> {
  const dek = getDek();
  if (!dek) return 0;
  let converted = 0;
  try {
    const db = await getOfflineDb();
    const rows = (await db.getAll('op_queue')) as StoredRow[];
    for (const row of rows) {
      if (isEnvelope((row as EncRow).env)) continue; // already encrypted
      const op = row as QueuedOp;
      await db.put('op_queue', await encodeRow(dek, op));
      converted += 1;
    }
    if (converted > 0) notify();
  } catch (e) {
    warn('reencryptLegacyOps', e);
  }
  return converted;
}

/** Count plaintext (un-encrypted) op_queue rows — verification helper (expect 0 post-pass). */
export async function countPlaintextOps(): Promise<number> {
  try {
    const db = await getOfflineDb();
    const rows = (await db.getAll('op_queue')) as StoredRow[];
    return rows.filter((r) => !isEnvelope((r as EncRow).env)).length;
  } catch { return 0; }
}

// ── Drain ─────────────────────────────────────────────────────────────────────

export interface DrainResult {
  drained: number;
  confirmed: number;
  conflicted: number;
  rejected: Array<{ op: QueuedOp; reason: string }>;
  complete: boolean;
  /** 5d Phase 4: the server signalled this device is revoked — caller must purge after. */
  purge: boolean;
}

interface SyncAck { client_op_id: string; outcome: 'confirmed' | 'conflicted' | 'rejected'; result_task_id?: string; reason?: string }

let draining = false;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export async function drainOpQueue(userId: string): Promise<DrainResult> {
  const result: DrainResult = { drained: 0, confirmed: 0, conflicted: 0, rejected: [], complete: false, purge: false };
  if (draining) return result;
  const dek = getDek();
  if (!dek) return result; // locked — cannot read the queue to drain it
  draining = true;
  try {
    let backoffMs = 2_000;
    for (let attempt = 0; ; ) {
      const ops = await listQueuedOps(userId);
      if (ops.length === 0) { result.complete = true; break; }
      const batch = ops.slice(0, SYNC_BATCH_MAX);

      let res;
      try {
        res = await apiFetch<{ acks: SyncAck[]; purge?: boolean }>('/api/sync/ops', {
          method: 'POST',
          body: JSON.stringify({
            ops: batch.map((op) => ({
              client_op_id: op.client_op_id, device_id: op.device_id, user_id: op.user_id,
              op_type: op.op_type, payload: op.payload, read_stamp: op.read_stamp, client_created_at: op.client_created_at,
            })),
          }),
        });
      } catch {
        break; // network gone again — queue intact
      }

      if (res.status === 429) {
        attempt += 1;
        if (attempt > 5) break;
        await sleep((res.retryAfterMs ?? backoffMs) + Math.random() * 1_000);
        backoffMs = Math.min(backoffMs * 2, 60_000);
        continue;
      }
      if (res.error || !res.data) break;

      // 5d Phase 4: a revoked device gets ONE grace drain (server applied this batch),
      // then `purge`. Apply the acks for this batch, flag purge, and STOP — the caller
      // deletes the DB. (On a subsequent revoked contact the server returns empty acks +
      // purge, so no further ops drain.)
      const acks = new Map(res.data.acks.map((a) => [a.client_op_id, a]));
      const db = await getOfflineDb();
      for (const op of batch) {
        const ack = acks.get(op.client_op_id);
        if (!ack) continue;
        await db.delete('op_queue', op.key);
        result.drained += 1;
        if (ack.outcome === 'confirmed') result.confirmed += 1;
        else if (ack.outcome === 'conflicted') result.conflicted += 1;
        else result.rejected.push({ op, reason: ack.reason ?? 'Rejected.' });
      }
      notify();
      if (res.data.purge) { result.purge = true; break; } // grace drain done → purge
      attempt = 0;
      backoffMs = 2_000;
    }
  } catch (e) {
    warn('drainOpQueue', e);
  } finally {
    draining = false;
  }
  return result;
}
